/**
 * useFavorites Hook
 * 
 * Verwaltet Favoriten mit API-Persistierung für eingeloggte User.
 * Anonyme User werden zur Registrierung aufgefordert.
 * 
 * @module client/hooks/use-favorites
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { apiRequest } from '../lib/queryClient';
import { useToast } from './use-toast';
import { useLocation } from 'wouter';

interface Favorite {
  id: number;
  userId: number;
  restaurantId: string;
  createdAt: string;
}

export function useFavorites() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  
  const isAuthenticated = !!user;
  
  // Favoriten-Liste abrufen (nur authentifiziert)
  const { data: serverFavorites = [], isLoading } = useQuery<Favorite[]>({
    queryKey: ['/api/favorites'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/favorites');
      if (!res.ok) throw new Error('Fehler beim Laden der Favoriten');
      return res.json();
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 Minuten
  });
  
  // Kombinierte Favoriten-IDs (nur aus Server)
  const favoriteIds = useMemo(() => {
    if (isAuthenticated) {
      return serverFavorites.map(f => f.restaurantId);
    }
    return [];
  }, [isAuthenticated, serverFavorites]);
  
  // Favorit hinzufügen (Mutation)
  const addMutation = useMutation({
    mutationFn: async (restaurantId: string) => {
      const res = await apiRequest('POST', `/api/restaurants/${restaurantId}/favorite`);
      if (!res.ok) {
        const error = await res.json().catch(() => ({ message: 'Fehler' }));
        throw new Error(error.message || 'Konnte nicht zu Favoriten hinzufügen');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/favorites'] });
      queryClient.invalidateQueries({ queryKey: ['/api/users/favorites'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Favorit entfernen (Mutation)
  const removeMutation = useMutation({
    mutationFn: async (restaurantId: string) => {
      const res = await apiRequest('DELETE', `/api/restaurants/${restaurantId}/favorite`);
      if (!res.ok && res.status !== 204) {
        const error = await res.json().catch(() => ({ message: 'Fehler' }));
        throw new Error(error.message || 'Konnte nicht aus Favoriten entfernen');
      }
      return { restaurantId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/favorites'] });
      queryClient.invalidateQueries({ queryKey: ['/api/users/favorites'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
  
  // Prüfen ob Restaurant ein Favorit ist
  const isFavorite = useCallback((restaurantId: string): boolean => {
    return favoriteIds.includes(restaurantId);
  }, [favoriteIds]);
  
  // Toggle-Funktion — erfordert Authentifizierung
  const toggleFavorite = useCallback((restaurantId: string) => {
    if (!isAuthenticated) {
      toast({
        title: 'Anmeldung erforderlich',
        description: 'Melde dich an, um Restaurants als Favoriten zu speichern.',
      });
      navigate('/auth');
      return;
    }
    
    if (isFavorite(restaurantId)) {
      removeMutation.mutate(restaurantId);
    } else {
      addMutation.mutate(restaurantId);
    }
  }, [isAuthenticated, isFavorite, addMutation, removeMutation, toast, navigate]);
  
  return {
    favoriteIds,
    isFavorite,
    toggleFavorite,
    addFavorite: addMutation.mutate,
    removeFavorite: removeMutation.mutate,
    isLoading,
    isToggling: addMutation.isPending || removeMutation.isPending,
  };
}

/**
 * Einfacher Hook für einzelne Restaurant-Karten
 * Optimiert für Performance mit stabilen Referenzen
 */
export function useRestaurantFavorite(restaurantId: string) {
  const { isFavorite, toggleFavorite, isToggling } = useFavorites();
  
  const isFav = useMemo(() => isFavorite(restaurantId), [isFavorite, restaurantId]);
  
  const toggle = useCallback(() => {
    toggleFavorite(restaurantId);
  }, [toggleFavorite, restaurantId]);
  
  return {
    isFavorite: isFav,
    toggle,
    isToggling,
  };
}
